import {Component, computed, inject} from '@angular/core';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {TimesheetTypeSummary} from '../../../../../../../api';
import {TimesheetTypeFormModel} from '../../../../../../features/timesheets/models/timesheet-type-form-model';
import {TimesheetTypeQueryService} from '../../../../../../features/timesheets/services/timesheet-type-query.service';
import {
  TimesheetTypeCommandService
} from '../../../../../../features/timesheets/services/timesheet-type-command.service';
import {TextInput} from '../../../../../../components/text-input/text-input';
import {ElciaButton} from '../../../../../../components/buttons/button/button';
import {ProductDropdown} from '../../../../../products/components/product-dropdown/product-dropdown';
import {ToggleSwitch} from '../../../../../../components/toggle-switch/toggle-switch';

@Component({
  selector: 'elcia-timesheet-settings-type',
  imports: [TextInput, ElciaButton, TranslatePipe, ProductDropdown, ToggleSwitch],
  templateUrl: './timesheet-settings-type.html',
  styleUrl: './timesheet-settings-type.scss',
})
export class TimesheetSettingsType {

  private readonly queryService = inject(TimesheetTypeQueryService);
  private readonly commandService = inject(TimesheetTypeCommandService);

  isLoading = computed(() => this.queryService.isLoading());

  typeForms = computed(() => {
    const formArray = new FormArray<FormGroup<TimesheetTypeFormModel>>([]);
    for (const type of this.queryService.types()) {
      const fg = this.createFormGroup(type);
      fg.disable();
      formArray.push(fg);
    }
    return formArray;
  });

  createForm = new FormGroup<Omit<TimesheetTypeFormModel, 'id'>>({
    nameEn: new FormControl('', Validators.required),
    nameFr: new FormControl('', Validators.required),
    nameNl: new FormControl('', Validators.required),
    productId: new FormControl(),
    hasTravelExpenses: new FormControl(false, {nonNullable: true}),
  });

  private createFormGroup(type: TimesheetTypeSummary): FormGroup<TimesheetTypeFormModel> {
    return new FormGroup<TimesheetTypeFormModel>({
      id: new FormControl(type.id),
      nameEn: new FormControl(type.nameEn ?? '', Validators.required),
      nameFr: new FormControl(type.nameFr ?? '', Validators.required),
      nameNl: new FormControl(type.nameNl ?? '', Validators.required),
      productId: new FormControl(type.productId ?? null),
      hasTravelExpenses: new FormControl(type.hasTravelExpenses ?? false, {nonNullable: true}),
    });
  }

  private getFormGroupById(id: number | null | undefined) {
    return this.typeForms().controls.find(c => c.value.id === id);
  }

  toggleFormGroup(id: number | null | undefined) {
    const fg = this.getFormGroupById(id);
    if (!fg) return;
    if (fg.enabled) {
      if (fg.invalid) return;
      this.commandService.updateMutation.mutate({
        id: fg.value.id!,
        body: {
          nameFr: fg.value.nameFr,
          nameEn: fg.value.nameEn,
          nameNl: fg.value.nameNl,
          productId: fg.value.productId,
          hasTravelExpenses: fg.value.hasTravelExpenses ?? false
        },
      });
      fg.disable();
    } else {
      fg.enable();
    }
  }

  deleteType(id: number | null | undefined) {
    if (id == null) return;
    this.commandService.deleteMutation.mutate(id);
  }

  submitCreate() {
    if (this.createForm.invalid) return;
    this.commandService.createMutation.mutate(
      {
        nameFr: this.createForm.value.nameFr,
        nameEn: this.createForm.value.nameEn,
        nameNl: this.createForm.value.nameNl,
        productId: this.createForm.value.productId,
        hasTravelExpenses: this.createForm.value.hasTravelExpenses ?? false
      },
      {onSuccess: () => this.createForm.reset()}
    );
  }
}
